import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, Empty } from "@/components/Shared";
import { formatINR } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Search, Upload, Download, Plus, ChevronRight, Loader2, ImagePlus } from "lucide-react";
import { ProductThumb } from "@/pages/QuoteBuilder";

const emptyForm = { code: "", name: "", category: "Wires", brand: "", unit: "Nos",
  hsn: "", gst_rate: 0.18, list_price: 0, minimum_price: 0, purchase_price: 0, active: true };
const marginPct = (p) => (p.list_price ? ((p.list_price - (p.purchase_price || 0)) / p.list_price) * 100 : 0);

export default function Products() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const photoRef = useRef();
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoResult, setPhotoResult] = useState(null);
  const canEdit = user?.is_super_admin || ["Owner", "Manager"].includes(user?.role);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page, page_size: 40 });
    if (q) params.set("q", q);
    if (category) params.set("category", category);
    if (brand) params.set("brand", brand);
    const { data } = await api.get(`/products?${params}`);
    setData(data);
  }, [page, q, category, brand]);
  const loadMeta = async () => setMeta((await api.get("/products/meta")).data);

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/products", form);
      toast.success("Product added");
      setOpen(false); setForm(emptyForm); loadMeta(); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const doImport = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/products/import", fd);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
      loadMeta(); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doExport = async () => {
    const res = await api.get("/products/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "products.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const doBulkPhotos = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    setPhotoBusy(true); setPhotoResult(null);
    try {
      const { data } = await api.post("/products/images/bulk", fd);
      setPhotoResult(data);
      toast.success(`${data.matched} photo(s) linked`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setPhotoBusy(false); if (photoRef.current) photoRef.current.value = ""; }
  };

  return (
    <div>
      <PageHeader title="Products" subtitle="Your catalogue and price books"
        actions={canEdit && (
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
              onChange={(e) => e.target.files[0] && doImport(e.target.files[0])} data-testid="product-import-input" />
            <Button variant="outline" className="h-10" onClick={() => fileRef.current?.click()} data-testid="product-import-btn">
              <Upload className="w-4 h-4" /> Import
            </Button>
            <Button variant="outline" className="h-10" onClick={doExport} data-testid="product-export-btn">
              <Download className="w-4 h-4" /> Export
            </Button>
            <Dialog open={photoOpen} onOpenChange={(v) => { setPhotoOpen(v); if (!v) setPhotoResult(null); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="h-10" data-testid="product-photos-btn"><ImagePlus className="w-4 h-4" /> Photos</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Bulk product photos</DialogTitle></DialogHeader>
                <div className="space-y-3 text-sm">
                  <p className="text-[#6B7189]">Name each image file after the product <b>code</b> — e.g. <span className="font-mono text-[#101D51]">FAN-CEIL.jpg</span>. We match and attach them automatically so quotes show real photos. JPG, PNG, WEBP or GIF, up to 5 MB each.</p>
                  <input ref={photoRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={(e) => e.target.files.length && doBulkPhotos(e.target.files)} data-testid="product-photos-input" />
                  <Button onClick={() => photoRef.current?.click()} disabled={photoBusy}
                    className="w-full h-11 bg-[#0B7C5A] hover:bg-[#09664a]" data-testid="product-photos-select-btn">
                    {photoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4" /> Choose images</>}
                  </Button>
                  {photoResult && (
                    <div className="rounded-lg border border-[#E6E8F0] p-3 space-y-1.5" data-testid="product-photos-result">
                      <div className="text-[#0B7C5A] font-semibold">{photoResult.matched} photo(s) linked</div>
                      {photoResult.unmatched?.length > 0 && <div className="text-[#6B7189]"><b>No matching code:</b> {photoResult.unmatched.join(", ")}</div>}
                      {photoResult.errors?.length > 0 && <div className="text-[#F83A84]">{photoResult.errors.join("; ")}</div>}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="h-10 bg-[#0B7C5A] hover:bg-[#09664a]" data-testid="product-add-btn"><Plus className="w-4 h-4" /> Add</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>New product</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Code"><Input data-testid="product-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
                  <Field label="Name"><Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                  <Field label="Category">
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="product-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{meta?.all_categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Brand"><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></Field>
                  <Field label="Unit">
                    <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{meta?.units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="HSN"><Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} /></Field>
                  <Field label="Barcode (EAN/UPC)"><Input data-testid="product-barcode" value={form.barcode || ""} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="Scan or type" /></Field>
                  <Field label="GST rate">
                    <Select value={String(form.gst_rate)} onValueChange={(v) => setForm({ ...form, gst_rate: Number(v) })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{[0, 0.05, 0.12, 0.18, 0.28].map((g) => <SelectItem key={g} value={String(g)}>{g * 100}%</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="List price (₹)"><Input type="number" data-testid="product-list-price" value={form.list_price} onChange={(e) => setForm({ ...form, list_price: Number(e.target.value) })} /></Field>
                  <Field label="Minimum price (₹)"><Input type="number" value={form.minimum_price} onChange={(e) => setForm({ ...form, minimum_price: Number(e.target.value) })} /></Field>
                  <Field label="Purchase price (₹)"><Input type="number" data-testid="product-purchase-price" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: Number(e.target.value) })} /></Field>
                  <Field label="Margin"><div className="h-10 flex items-center text-sm font-semibold text-[#0B7C5A]" data-testid="product-form-margin">{formatINR(Math.round((form.list_price - form.purchase_price) * 100))} ({marginPct({ list_price: form.list_price, purchase_price: form.purchase_price }).toFixed(1)}%)</div></Field>
                </div>
                <DialogFooter>
                  <Button onClick={save} disabled={saving} className="bg-[#101D51] hover:bg-[#080852]" data-testid="product-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save product"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )} />

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
        <Chip active={!category} onClick={() => { setCategory(""); setPage(1); }}>All</Chip>
        {meta?.categories.map((c) => (
          <Chip key={c.name} active={category === c.name} onClick={() => { setCategory(c.name); setPage(1); }}>
            {c.name} <span className="opacity-60">{c.count}</span>
          </Chip>
        ))}
      </div>

      {/* Search + brand */}
      <div className="flex flex-col sm:flex-row gap-2 mt-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />
          <Input data-testid="product-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search name, code or brand…" className="pl-9 h-10 bg-white" />
        </div>
        <Select value={brand || "all"} onValueChange={(v) => { setBrand(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="h-10 w-full sm:w-52 bg-white" data-testid="product-brand-filter"><SelectValue placeholder="All brands" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All brands</SelectItem>
            {meta?.brands.map((b) => <SelectItem key={b.name} value={b.name}>{b.name} ({b.count})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!data ? <Loading /> : data.items.length === 0 ? (
        <Empty icon={Package} title="No products found" hint="Try a different search or import your catalogue from Excel." />
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden sm:block overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F6FA] text-[#6B7189]">
                <tr className="text-left">
                  <th className="px-4 py-3 font-medium w-14"></th>
                  <th className="px-4 py-3 font-medium">Code</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Brand</th>
                  <th className="px-4 py-3 font-medium">Unit</th>
                  <th className="px-4 py-3 font-medium text-right">Purchase</th>
                  <th className="px-4 py-3 font-medium text-right">List price</th>
                  <th className="px-4 py-3 font-medium text-right">Margin</th>
                  <th className="px-4 py-3 font-medium text-right">GST</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <tr key={p.id} className="border-t border-[#E6E8F0] hover:bg-[#F5F6FA] cursor-pointer"
                    onClick={() => navigate(`/products/${p.id}`)} data-testid={`product-row-${p.code}`}>
                    <td className="px-4 py-2"><ProductThumb url={p.image_url} size={9} /></td>
                    <td className="px-4 py-3 font-mono text-xs">{p.code}</td>
                    <td className="px-4 py-3 font-medium text-[#101D51]">{p.name}</td>
                    <td className="px-4 py-3 text-[#6B7189]">{p.brand}</td>
                    <td className="px-4 py-3 text-[#6B7189]">{p.unit}</td>
                    <td className="px-4 py-3 text-right text-[#6B7189]">{p.purchase_price ? formatINR(p.purchase_price) : "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold">{formatINR(p.list_price)}</td>
                    <td className={`px-4 py-3 text-right text-xs font-medium ${p.purchase_price ? "text-[#0B7C5A]" : "text-[#6B7189]"}`}>{p.purchase_price ? `${marginPct(p).toFixed(1)}%` : "—"}</td>
                    <td className="px-4 py-3 text-right text-[#6B7189]">{Math.round(p.gst_rate * 100)}%</td>
                    <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-[#6B7189]" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Mobile cards */}
          <div className="sm:hidden space-y-2">
            {data.items.map((p) => (
              <Card key={p.id} className="p-3 active:bg-[#F5F6FA] flex items-center gap-3" onClick={() => navigate(`/products/${p.id}`)} data-testid={`product-card-${p.code}`}>
                <ProductThumb url={p.image_url} size={10} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#101D51] truncate leading-tight">{p.name}</div>
                  <div className="text-[11px] text-[#6B7189] truncate">{p.brand ? `${p.brand} · ` : ""}{p.code}</div>
                  {p.purchase_price ? <div className="text-[11px] text-[#0B7C5A]">cost {formatINR(p.purchase_price)} · {marginPct(p).toFixed(0)}%</div> : null}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-[#101D51] whitespace-nowrap">{formatINR(p.list_price)}</div>
                  <div className="text-[10px] text-[#6B7189]">{p.unit} · {Math.round(p.gst_rate * 100)}%</div>
                </div>
              </Card>
            ))}
          </div>

          <Pager data={data} page={page} setPage={setPage} />
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs font-medium text-[#6B7189]">{label}</label><div className="mt-1">{children}</div></div>;
}
function Chip({ active, children, ...p }) {
  return (
    <button {...p} className={`shrink-0 min-h-[40px] px-4 rounded-full text-sm font-medium border transition-colors
      ${active ? "bg-[#101D51] text-white border-[#101D51]" : "bg-white text-[#6B7189] border-[#E6E8F0] hover:border-[#101D51]"}`}>
      {children}
    </button>
  );
}
export function Pager({ data, page, setPage }) {
  if (data.pages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-sm">
      <span className="text-[#6B7189]">Page {data.page} of {data.pages} · {data.total} items</span>
      <div className="flex gap-2">
        <Button variant="outline" className="h-9" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
        <Button variant="outline" className="h-9" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>Next</Button>
      </div>
    </div>
  );
}
